// src/share/ShareViewer.jsx
// FIXED: Now handles both storage-based (S-) and inline-encoded share links
import React, { useState, useEffect } from "react";
import { decodeAlbumPayload, isStoredShareHash, loadStoredAlbumFromHash } from "../utils/share-v2";

export default function ShareViewer() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadProgress, setLoadProgress] = useState({ phase: 'init', loaded: 0, total: 0 });

  useEffect(() => {
    async function loadAlbum() {
      const hash = window.location.hash?.replace(/^#/, "");
      
      if (!hash) {
        setError('No album data in URL');
        setLoading(false);
        return;
      }
      
      try {
        // Check if it's a storage-based share (new format starting with S-)
        if (isStoredShareHash(hash)) {
          console.log('[ShareViewer] Loading storage-based album...');
          const albumData = await loadStoredAlbumFromHash(hash, (progress) => {
            setLoadProgress(progress);
            console.log('[ShareViewer] Progress:', progress);
          });
          
          if (albumData) {
            console.log('[ShareViewer] Album loaded:', {
              pages: albumData.pages?.length,
              photos: albumData.photos?.length
            });
            setPayload(albumData);
          } else {
            setError('Failed to load album from storage');
          }
        } else {
          // Try inline decode (legacy format)
          console.log('[ShareViewer] Trying inline decode...');
          const decoded = decodeAlbumPayload(hash);
          if (decoded) {
            setPayload(decoded);
          } else {
            setError('Invalid share link format');
          }
        }
      } catch (err) {
        console.error('[ShareViewer] Load error:', err);
        setError(err.message || 'Failed to load album');
      } finally {
        setLoading(false);
      }
    }
    
    loadAlbum();
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4 animate-pulse">📚</div>
          <h1 className="text-2xl font-bold mb-2">Loading Album...</h1>
          {loadProgress.phase === 'photos' && loadProgress.total > 0 && (
            <div className="mt-4">
              <div className="w-64 mx-auto bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(loadProgress.loaded / loadProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-slate-400 text-sm mt-2">
                Loading photos: {loadProgress.loaded} / {loadProgress.total}
              </p>
            </div>
          )}
          {loadProgress.phase === 'structure' && (
            <p className="text-slate-400 text-sm mt-2">Loading album structure...</p>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error || !payload) {
    return (
      <div className="min-h-screen bg-slate-900 text-white p-6 flex items-center justify-center">
        <div className="max-w-md text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold mb-2">Unable to Load Album</h1>
          <p className="text-slate-400 mb-4">
            {error || 'This share link appears to be invalid or corrupted. Please ask the sender to share the album again.'}
          </p>
          <div className="text-slate-500 text-xs mb-6">
            Error details: {error}
          </div>
          <a 
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Create Your Own Album
          </a>
        </div>
      </div>
    );
  }

  const { trip, pages, title } = payload;
  const tripName = trip?.name || title || "Shared Album";
  const tripDates = trip?.dates || "";
  const tripRoute = trip?.route || "";

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-slate-800 border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold mb-2">{tripName}</h1>
          {tripDates && (
            <p className="text-slate-400 text-sm">{tripDates}</p>
          )}
          {tripRoute && (
            <p className="text-slate-300 text-sm mt-1">{tripRoute}</p>
          )}
          <p className="text-slate-500 text-sm mt-2">
            {pages?.length || 0} {pages?.length === 1 ? 'page' : 'pages'}
          </p>
        </div>
      </div>

      {/* Album Pages */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-8">
          {pages?.map((p, i) => {
            // Extract photos from various possible structures
            let photos = [];
            
            // New format: {photos: [{src, url, alt}]}
            if (Array.isArray(p.photos)) {
              photos = p.photos.map(photo => ({
                src: photo.src || photo.url,
                alt: photo.alt || `Photo ${i + 1}`
              })).filter(photo => photo.src);
            }
            // Legacy format: {items: [{photoId, url, storagePath}]}
            else if (Array.isArray(p.items)) {
              photos = p.items.map(item => ({
                src: item.url || item.src,
                alt: item.alt || `Photo ${i + 1}`
              })).filter(photo => photo.src);
            }

            const caption = p.caption || p.meta?.caption || p.meta?.title || p.meta?.text || '';
            const pageType = p.type || p.templateKey || 'standard';
            
            return (
              <div 
                key={i} 
                className="bg-slate-800 rounded-2xl overflow-hidden shadow-xl border border-slate-700"
              >
                {/* Page Header */}
                <div className="p-6 border-b border-slate-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-white">
                        Page {i + 1}
                      </h2>
                      {pageType !== 'standard' && (
                        <span className="text-xs text-slate-400 mt-1 inline-block capitalize">
                          {pageType.replace(/[-_]/g, ' ')}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-slate-400">
                      {photos.length} {photos.length === 1 ? 'photo' : 'photos'}
                    </span>
                  </div>
                  {caption && (
                    <div 
                      className="mt-3 text-slate-300 prose prose-invert max-w-none"
                      dangerouslySetInnerHTML={{ __html: caption }}
                    />
                  )}
                </div>

                {/* Photos Grid */}
                {photos.length > 0 && (
                  <div className="p-4">
                    <div className={`grid gap-3 ${
                      photos.length === 1 ? 'grid-cols-1' : 
                      photos.length === 2 ? 'grid-cols-2' : 
                      photos.length === 3 ? 'grid-cols-3' : 
                      photos.length === 4 ? 'grid-cols-2' : 
                      'grid-cols-3'
                    }`}>
                      {photos.slice(0, 12).map((ph, k) => (
                        <div
                          key={k}
                          className="relative aspect-square bg-slate-700 rounded-lg overflow-hidden group"
                        >
                          <img
                            src={ph.src}
                            alt={ph.alt || `Photo ${k + 1}`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              console.warn('Failed to load image:', ph.src?.substring(0, 100));
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {photos.length > 12 && (
                      <p className="text-center text-slate-500 text-sm mt-4">
                        + {photos.length - 12} more {photos.length - 12 === 1 ? 'photo' : 'photos'}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Fallback for special pages without photos */}
                {photos.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <div className="text-4xl mb-2">
                      {pageType === 'map' ? '🗺️' : 
                       pageType === 'stats' ? '📊' : 
                       pageType === 'timeline' ? '📅' :
                       pageType === 'cover' ? '📖' : '📄'}
                    </div>
                    <p className="capitalize">{pageType.replace(/[-_]/g, ' ')} Page</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm mb-4">Shared via VivaJour</p>
          <a 
            href="/"
            className="inline-block px-6 py-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors text-white font-medium"
          >
            Create Your Own Travel Album
          </a>
        </div>
      </div>
    </div>
  );
}